// Pinia store for transient notifications.
//
// Replaces native `alert()` calls. Toasts auto-dismiss after a few
// seconds (the kind decides the duration); a close button is always
// available. Errors stick longer than success messages because an
// operator might need to read them before acting.
//
// Usage in any view:
//
//     import { useToastStore } from '@/stores/toasts';
//     const toasts = useToastStore();
//     toasts.error('Save failed: ' + msg);
//     toasts.info('Schedule updated.');
//     toasts.warn('You’re editing a paused schedule.');
//
// One <ToastStack /> mounted in AppShell renders all active toasts.

import { defineStore } from 'pinia';
import { ref } from 'vue';

export type ToastKind = 'info' | 'success' | 'warn' | 'error';

export interface Toast {
  id:   number;
  kind: ToastKind;
  text: string;
  /** Auto-dismiss after this many ms; 0 = sticky (close button only). The Reka
   *  ToastRoot in <ToastStack/> owns the timer (so it pauses on hover/focus and
   *  supports swipe-to-dismiss); the store just holds the list. */
  duration: number;
}

const DEFAULT_TTL_MS: Record<ToastKind, number> = {
  info:    4_000,
  success: 4_000,
  warn:    6_000,
  // Errors hover longer - by the time someone reads it they may need
  // to re-do an action and want the message visible while doing so.
  error:   10_000,
};

export const useToastStore = defineStore('toasts', () => {
  const list = ref<Toast[]>([]);
  let next = 1;

  function push(kind: ToastKind, text: string, ttlMs?: number): number {
    const id = next++;
    const duration = ttlMs ?? DEFAULT_TTL_MS[kind];
    list.value = [...list.value, { id, kind, text, duration }];
    return id;
  }
  function dismiss(id: number): void {
    list.value = list.value.filter((t) => t.id !== id);
  }

  return {
    list,
    info:    (text: string, ttl?: number) => push('info',    text, ttl),
    success: (text: string, ttl?: number) => push('success', text, ttl),
    warn:    (text: string, ttl?: number) => push('warn',    text, ttl),
    error:   (text: string, ttl?: number) => push('error',   text, ttl),
    dismiss,
  };
});
